import React, { useMemo, useCallback } from "react";
import { format } from "lib/globalize-util";
import MenuItem from "@material-ui/core/MenuItem";
import { ICellRendererParams } from "ag-grid-community";
import MoreMenu from "./MoreMenu";
import {
    viewInventory,
    handleBuildOrderOpen,
    handleManageDeliveries,
    removeLineItems,
    updateWorkingOrder,
    removeProducts,
    showCartConfirmation,
} from "actions/cart-legacy";
import { IStore } from "reducers";
import { connect } from "react-redux";
import type { IScope } from "reducers/scope";
import { postToWindow, postToTopIframe } from "helpers/window-messages";
import _ from "lodash";
import { DATATEST } from "constants/integration";
import me from "models/me";
import { hasInventory } from "components/product/inventory-helpers";
import type { IProduct } from "types/product";

const { PRODUCT_MENU } = DATATEST.GRID_VIEW;
type StateProps = {
    scope: IScope;
    products: IProduct[];
    productWarehouses: { [productId: string]: { default: string; available: string[] } };
    isBulkOrder?: boolean;
};
export type IProps = ICellRendererParams & StateProps;

const ProductMenu = (props: IProps) => {
    const { scope, products, isBulkOrder, node, productWarehouses, data = {} } = props;

    const product = useMemo<Partial<IProduct>>(
        () => _.find(products, { _id: data.productId }),
        [products, data.productId],
    );

    // Todo: Check real future inventory using product.__availability
    const displayInventory = useMemo<boolean>(
        () =>
            me.hasPermission("display_inventory") && hasInventory(product) && _.includes(product.__inventory, "Future"),
        [product],
    );
    const buildOrderEnabled = useMemo<boolean>(() => scope?.brand?.hasPreference("build_order"), [scope]);
    const lineItemIds = useMemo(() => _.uniq(_.map(node?.data?.entries, "cartLineItemId")), [node?.data?.entries]);

    const onManageDeliveries = useCallback(() => {
        if (buildOrderEnabled) {
            postToTopIframe(handleBuildOrderOpen());
        } else {
            postToWindow(handleManageDeliveries({ product: data?.productId }));
        }
    }, [buildOrderEnabled, data?.productId]);

    const onViewInventory = useCallback(() => {
        postToTopIframe(
            viewInventory(product, {
                availableWarehouses: productWarehouses?.[product._id].available,
            }),
        );
    }, [productWarehouses, product]);

    const handleRemoveLineItem = useCallback(() => {
        if (!isBulkOrder) {
            postToWindow(updateWorkingOrder(removeLineItems(lineItemIds)));
            return;
        }
        postToTopIframe(
            showCartConfirmation({
                title: format("Remove product(s)?"),
                message: format(
                    "Removing the selected product(s) will remove them from all locations in your order. To keep product(s) in other locations, select Cancel, and then change the product order quantities to zero for this location",
                ),
                confirmText: format("Remove product(s)"),
                cancelText: format("Cancel"),
                isError: true,
                action: updateWorkingOrder(removeProducts(node.data?.productId)),
            }),
        );
    }, [isBulkOrder, lineItemIds, node.data?.productId]);

    return (
        <>
            <MoreMenu closeOnClick>
                <MenuItem onClick={onManageDeliveries} data-test={PRODUCT_MENU.MANAGE_DELIVERIES}>
                    {format("Manage deliveries")}
                </MenuItem>
                {displayInventory && (
                    <MenuItem onClick={onViewInventory} data-test={PRODUCT_MENU.VIEW_INVENTORY}>
                        {format("View inventory")}
                    </MenuItem>
                )}
                <MenuItem style={{ display: "none" }} disabled={true} data-test={PRODUCT_MENU.DUPLICATE}>
                    {format("Duplicate")}
                </MenuItem>
                <MenuItem onClick={handleRemoveLineItem} data-test={PRODUCT_MENU.REMOVE_PRODUCT}>
                    {format("Remove product")}
                </MenuItem>
            </MoreMenu>
        </>
    );
};

const mapStateToProps = (store: IStore, props: IProps): StateProps => ({
    scope: store.scope,
    products: store.cartGridView?.data?.products,
    productWarehouses: store.cartGridView?.data?.productWarehouses,
    isBulkOrder: store.cartGridView?.data?.company?.allow_bulk,
});

export default connect<StateProps, null, ICellRendererParams>(mapStateToProps)(ProductMenu);
